import axios from "axios";
export const SESSION_KEY = "technogenious-checkout-v1";
export type Session = {
  cartId: string;
  guestToken?: string;
  ownerId?: string;
  locked?: boolean;
};
export type ShippingOption = {
  id: string;
  label: string;
  amountCents: number;
  currency: string;
};
export type Payment = {
  clientSecret: string;
  amountCents: number;
  currency: string;
};
const base = () => {
  const url = process.env.REACT_APP_SERVER_URL;
  if (!url)
    throw new Error("Checkout is not configured. Please contact the store.");
  return url.replace(/\/$/, "");
};
export function readSession(): Session | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (!value || !/^[a-f0-9]{24}$/i.test(value.cartId)) return null;
    if (value.guestToken && /^[a-f0-9]{64}$/.test(value.guestToken))
      return value;
    if (value.ownerId && /^[a-f0-9]{24}$/i.test(value.ownerId)) return value;
    return null;
  } catch {
    return null;
  }
}
export function saveSession(session: Session) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}
export function clearSession() {
  sessionStorage.removeItem(SESSION_KEY);
}
export function headers(session: Session, user: any) {
  if (session.guestToken)
    throw new Error("Return to your cart to start checkout while signed in.");
  if (!user?.access_token || user.user?._id !== session.ownerId)
    throw new Error("Sign in to the account that started this checkout.");
  return { Authorization: `Bearer ${user.access_token}` };
}
export function errorMessage(error: any) {
  const status = error?.response?.status;
  if (status === 401)
    return "Your sign-in has expired. Sign in again to continue.";
  if (status === 404)
    return "This cart is unavailable. Check your account or return to your cart.";
  if (status === 409)
    return (
      error?.response?.data?.message ||
      "This checkout changed. Reload it before continuing."
    );
  const message = error?.response?.data?.message;
  if (status === 400 && typeof message === "string") return message;
  if (status >= 500)
    return "Checkout is temporarily unavailable. Please try again; your existing payment will be reused.";
  return error instanceof Error && !axios.isAxiosError(error)
    ? error.message
    : "We could not connect. Check your connection and try again.";
}
export const money = (cents: number, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
export const checkoutApi = {
  async create(products: any[], user: any): Promise<Session> {
    const token = user?.access_token;
    if (!token || !user.user?._id)
      throw new Error("Sign in before starting checkout.");
    const result = await axios.post(
      `${base()}/cart/cart-v2`,
      { product: products.map(({ id, quantity, optionId }) => ({ id, quantity, ...(optionId?{optionId}:{}) })) },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const cart = result.data.data;
    const session: Session = { cartId: cart._id, ownerId: user.user._id };
    saveSession(session);
    return session;
  },
  async update(session: Session, products: any[], user: any) {
    return (
      await axios.patch(
        `${base()}/cart/cart-v2/${session.cartId}`,
        { product: products.map(({ id, quantity, optionId }) => ({ id, quantity, ...(optionId?{optionId}:{}) })) },
        { headers: headers(session, user) }
      )
    ).data.data;
  },
  async cart(session: Session, user: any) {
    return (
      await axios.get(`${base()}/cart/${session.cartId}`, {
        headers: headers(session, user),
      })
    ).data.data;
  },
  async reopen(session: Session, user: any, bankTransferNotSent = false) {
    await axios.post(`${base()}/cart/reopen/${session.cartId}`, bankTransferNotSent ? { bankTransferNotSent: true } : {}, { headers: headers(session, user) });
    const editable = { ...session, locked: false };
    saveSession(editable);
    return editable;
  },
  async shipping(): Promise<ShippingOption[]> {
    return (await axios.get(`${base()}/cart/shipping-options`)).data.data;
  },
  async details(session: Session, user: any, body: any) {
    await axios.post(`${base()}/cart/check-out/${session.cartId}`, body, {
      headers: headers(session, user),
    });
  },
  async payment(session: Session, user: any): Promise<Payment> {
    const payment = (
      await axios.post(
        `${base()}/cart/create-payment-intent/${session.cartId}`,
        {},
        { headers: headers(session, user) }
      )
    ).data.data;
    if (
      !payment?.clientSecret ||
      !Number.isSafeInteger(payment.amountCents) ||
      payment.amountCents <= 0 ||
      payment.currency !== "usd"
    )
      throw new Error(
        "The payment total could not be verified. Please contact the store."
      );
    return payment;
  },
};

export async function assertCartEditable(user?: any) {
  const session = readSession();
  if (session?.locked) await checkoutApi.reopen(session, user);
}
